/**
 * Ежедневная серия 7 дней.
 *
 * Решение E5: «день» — сутки UTC, сдвинутые на `StreakDef.dayStartHourUtc` (в v1 — 0, т. е. полночь UTC), одинаково
 * для всех игроков. Локальный часовой пояс не используем: его присылает клиент, а смена пояса позволила бы забрать
 * два дня за одни сутки; сервер — единственный источник времени.
 *
 * - Забрать можно раз в день серии. Забрал вчера — сегодня следующий день; после 7-го снова день 1 (круг).
 * - Пропустил хотя бы один день серии — снова день 1.
 * - Часы «назад» (сегодня раньше дня последнего получения) — считается, что сегодня уже забрано.
 */
import type { BalanceData, StreakState } from "@newfungame/contracts";

export const HOUR_MS = 3_600_000;
export const DAY_MS = 86_400_000;
export const STREAK_DAYS = 7;

/** Что сервер хранит о серии игрока. */
export interface StreakProgress {
  /** Номер дня серии ({@link streakDayIndex}), в который забрана последняя награда; `null` — ни разу. */
  lastClaimDayIndex: number | null;
  /** Какой день серии (1…7) был забран тогда; 0 — ни разу. */
  lastClaimedDay: number;
}

/** Серия нового игрока. */
export const STREAK_NEW: StreakProgress = { lastClaimDayIndex: null, lastClaimedDay: 0 };

/** Номер «дня серии» для момента `ms`: целые сутки от эпохи Unix со сдвигом `dayStartHourUtc`. */
export function streakDayIndex(b: BalanceData, ms: number): number {
  return Math.floor((ms - b.streak.dayStartHourUtc * HOUR_MS) / DAY_MS);
}

/** Начало дня серии с номером `dayIndex`, мс Unix. */
export function streakDayStartMs(b: BalanceData, dayIndex: number): number {
  return dayIndex * DAY_MS + b.streak.dayStartHourUtc * HOUR_MS;
}

/** Награда за день серии 1…7. */
export function streakReward(b: BalanceData, day: number): number {
  return b.streak.rewards[Math.min(STREAK_DAYS, Math.max(1, day)) - 1]!;
}

/**
 * Состояние серии на момент `nowMs` (поле `PlayerState.streak`). `expiresAt` — начало дня серии, к которому серия
 * сгорит, если её не продолжить: забрано сегодня — конец завтрашнего дня; не забрано — конец сегодняшнего.
 */
export function streakState(b: BalanceData, progress: StreakProgress, nowMs: number): StreakState {
  const today = streakDayIndex(b, nowMs);
  const last = progress.lastClaimDayIndex;
  if (last !== null && progress.lastClaimedDay >= 1 && today <= last) {
    const day = Math.min(STREAK_DAYS, progress.lastClaimedDay);
    return { day, claimedToday: true, todayReward: streakReward(b, day), expiresAt: streakDayStartMs(b, last + 2) };
  }
  const continues = last !== null && progress.lastClaimedDay >= 1 && today === last + 1;
  const day = continues ? (Math.min(STREAK_DAYS, progress.lastClaimedDay) % STREAK_DAYS) + 1 : 1;
  return { day, claimedToday: false, todayReward: streakReward(b, day), expiresAt: streakDayStartMs(b, today + 1) };
}

export type StreakClaim =
  { ok: true; day: number; coins: number; progress: StreakProgress } | { ok: false; code: "conflict" };

/** Забрать награду дня серии. Уже забрано сегодня — `conflict`. */
export function claimStreak(b: BalanceData, progress: StreakProgress, nowMs: number): StreakClaim {
  const s = streakState(b, progress, nowMs);
  if (s.claimedToday) return { ok: false, code: "conflict" };
  return {
    ok: true,
    day: s.day,
    coins: s.todayReward,
    progress: { lastClaimDayIndex: streakDayIndex(b, nowMs), lastClaimedDay: s.day },
  };
}
